import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { ArrowLeft, Ban, CheckCircle2, Download, Power } from "lucide-react";

const euro = (n) => `${(Number(n) || 0).toLocaleString("fr-FR")} €`;

const REF_OPTIONS = [
  { v: "nouveau_lead", label: "Nouveau lead" },
  { v: "contacte", label: "Contacté" },
  { v: "en_attente_inscription", label: "En attente d'inscription" },
  { v: "inscription_a_confirmer", label: "Inscription à confirmer" },
  { v: "inscription_validee", label: "Inscription validée" },
  { v: "inscription_refusee", label: "Inscription refusée" },
  { v: "inscription_annulee", label: "Inscription annulée" },
];
const REF_LABELS = Object.fromEntries(REF_OPTIONS.map((o) => [o.v, o.label]));

const COMM_OPTIONS = [
  { v: "en_attente", label: "En attente" },
  { v: "validee", label: "Validée" },
  { v: "a_payer", label: "À payer" },
  { v: "payee", label: "Payée" },
  { v: "annulee", label: "Annulée" },
];
const COMM_CLS = {
  en_attente: "bg-neutral-100 text-neutral-600",
  validee: "bg-blue-50 text-blue-700",
  a_payer: "bg-amber-50 text-amber-700",
  payee: "bg-emerald-50 text-emerald-700",
  annulee: "bg-red-50 text-red-700",
};

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid={testId}>
      <p className="text-xl font-black text-neutral-900">{value}</p>
      <p className="text-xs text-neutral-500">{label}</p>
    </div>
  );
}

export default function AdminAmbassadorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [commFilter, setCommFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/ambassadors/${id}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const patchAmb = async (fields, msg) => {
    try {
      await api.patch(`/admin/ambassadors/${id}`, fields);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const setReferral = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}/referral-status`, { referral_status: status });
      toast.success(`Statut mis à jour : ${REF_LABELS[status]}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const detach = async (leadId) => {
    try {
      await api.post(`/admin/leads/${leadId}/reassign-ambassador`, { ambassador_id: null });
      toast.success("Candidat détaché de l'ambassadeur.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const setCommStatus = async (commId, status) => {
    try {
      await api.patch(`/admin/ambassadors/commissions/${commId}/status`, { status });
      toast.success("Commission mise à jour.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const exportCsv = async () => {
    try {
      const params = { ambassador_id: id };
      if (monthFilter) params.month = monthFilter;
      const res = await api.get("/admin/ambassadors/commissions/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `commissions_${data.code}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!data) {
    return (
      <Shell role="admin">
        <div className="px-8 py-10 text-neutral-400">Chargement…</div>
      </Shell>
    );
  }

  const months = [...new Set((data.commissions || []).map((c) => c.month))];
  const commissions = (data.commissions || []).filter(
    (c) => (!commFilter || c.status === commFilter) && (!monthFilter || c.month === monthFilter)
  );

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <button onClick={() => navigate("/admin/ambassadeurs")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-brand" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tighter" data-testid="amb-detail-name">
              {data.first_name} {data.last_name}
            </h1>
            <p className="mt-1 font-mono text-lg font-semibold text-brand" data-testid="amb-detail-code">{data.code}</p>
            <p className="mt-1 text-sm text-neutral-500">{data.email} · {data.phone || "—"}</p>
            {data.payment_info && (
              <p className="mt-1 max-w-md text-xs text-neutral-400">Paiement : {data.payment_info}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => patchAmb({ code_active: !data.code_active }, data.code_active ? "Code désactivé." : "Code réactivé.")}
              data-testid="toggle-code-btn"
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${data.code_active ? "border-neutral-300 text-neutral-700 hover:border-brand" : "border-emerald-300 text-emerald-700"}`}
            >
              <Power className="h-4 w-4" /> {data.code_active ? "Désactiver le code" : "Réactiver le code"}
            </button>
            <button
              onClick={() => patchAmb({ suspended: !data.suspended }, data.suspended ? "Ambassadeur réactivé." : "Ambassadeur suspendu.")}
              data-testid="toggle-suspend-btn"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white ${data.suspended ? "bg-emerald-600" : "bg-red-500"}`}
            >
              {data.suspended ? <CheckCircle2 className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
              {data.suspended ? "Réactiver" : "Suspendre"}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Candidats" value={data.candidates_total} testId="ad-stat-candidates" />
          <Stat label="En attente" value={data.pending_inscriptions} testId="ad-stat-pending" />
          <Stat label="Validées" value={data.validated_inscriptions} testId="ad-stat-validated" />
          <Stat label="Commission due" value={euro(data.commission_validated)} testId="ad-stat-owed" />
          <Stat label="Payée" value={euro(data.commission_paid)} testId="ad-stat-paid" />
          <Stat label="Total gagné" value={euro(data.earned_total)} testId="ad-stat-total" />
        </div>

        {/* Candidats */}
        <h2 className="mt-10 text-lg font-bold text-neutral-800">Candidats apportés</h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="amb-candidates-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Candidat</th>
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Statut parrainage</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.candidates.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-neutral-400">Aucun candidat.</td></tr>
              ) : (
                data.candidates.map((c) => (
                  <tr key={c.id} data-testid={`cand-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/leads/${c.id}`} className="font-semibold text-neutral-800 hover:text-brand">
                        {c.first_name} {c.last_name}
                      </Link>
                      <div className="text-xs text-neutral-400">{c.city} · {c.phone}</div>
                      {c.referral_duplicate && (
                        <span className="mt-1 inline-block rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">DOUBLON — pas de commission</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{c.assigned_school_name || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={c.referral_status}
                        onChange={(e) => setReferral(c.id, e.target.value)}
                        disabled={c.referral_duplicate}
                        data-testid={`cand-status-${c.id}`}
                        className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-brand disabled:opacity-50"
                      >
                        {REF_OPTIONS.map((o) => (
                          <option key={o.v} value={o.v}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => detach(c.id)} data-testid={`cand-detach-${c.id}`} className="text-xs font-semibold text-red-500 hover:underline">
                        Détacher
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Commissions */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-neutral-800">Commissions</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="comm-month-filter" className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand">
              <option value="">Tous les mois</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={commFilter} onChange={(e) => setCommFilter(e.target.value)} data-testid="comm-status-filter" className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand">
              <option value="">Tous les statuts</option>
              {COMM_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="amb-commissions-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Mois</th>
                <th className="px-4 py-3">Candidat</th>
                <th className="px-4 py-3">Rang</th>
                <th className="px-4 py-3">Montant</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {commissions.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Aucune commission.</td></tr>
              ) : (
                commissions.map((c) => (
                  <tr key={c.id} data-testid={`comm-row-${c.id}`}>
                    <td className="px-4 py-3">{c.month}</td>
                    <td className="px-4 py-3 font-medium text-neutral-800">{c.candidate_name}</td>
                    <td className="px-4 py-3 text-neutral-500">#{c.rank}</td>
                    <td className="px-4 py-3 font-bold">{euro(c.amount)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={c.status}
                        onChange={(e) => setCommStatus(c.id, e.target.value)}
                        data-testid={`comm-status-${c.id}`}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold outline-none ${COMM_CLS[c.status]}`}
                      >
                        {COMM_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
